import { slateToMd } from '../Wysiwyg/slate-editor-helpers';
import { LIST_ITEM, splitEndingLists } from './commentLists';

// How a comment's text is written for saving, and read back for editing - the same rules for a new comment, an
// edited one, and the /requests portal, which all save through the same two server actions.
//
// Both actions run the body through Html2Markdown, which treats a line holding only spaces - non-breaking ones
// included - as blank and merges a run of them into one. So three empty lines typed between two others were saved
// as one. A zero-width space is not whitespace to it, so an empty line carrying one survives; the same trick keeps
// empty lines in automation messages for Teams (a946f7ddd).

const ZWSP = '\u200B';
const EMPTY_LINE = '&#8203;';

/** Holds only spaces, non-breaking spaces or zero-width spaces - an empty line however it was saved. */
const isEmptyLine = (line: string) => /^[ \t\u00A0\u200B]*$/.test(line.replace(/&nbsp;/g, '').replace(/&#8203;/g, ''));

const blockText = (node: any): string =>
  typeof node?.text === 'string' ? node.text : (node?.children ?? []).map(blockText).join('');
const isTypedEmptyLine = (block: any) =>
  block?.type === 'paragraph' && !(block.children ?? []).some((c: any) => typeof c.text !== 'string')
  && /^[\s\u200B\uFEFF]*$/.test(blockText(block));

/**
 * The editor's content as Markdown, one block at a time. Converting it in one go loses empty lines: turndown drops
 * the ones beside a list - two between a numbered and a bulleted list vanished - and merges the rest. Each typed
 * empty line here is its own empty line, for commentForSaving to keep.
 */
export const slateToComment = (value: any[]): string =>
  (value ?? []).map(block => (isTypedEmptyLine(block) ? '' : slateToMd([block]))).join('\n');

/**
 * Every line but the last ends in `&nbsp; ` so it shows as typed, as it always has. A typed empty line also carries
 * a zero-width space so the server keeps it. The blank line that ends a list stays truly blank, or the text after the
 * list is saved inside it.
 */
export const commentForSaving = (markdown: string): string => {
  // Empty lines at either end carry no spacing, and the editor adds one at the end every time a comment is opened.
  const all = splitEndingLists(markdown, true);
  const first = all.findIndex(line => line !== null && !isEmptyLine(line));
  const last = all.length - 1 - [...all].reverse().findIndex(line => line !== null && !isEmptyLine(line));
  const lines = first === -1 ? [] : all.slice(first, last + 1);
  return lines.map((line, i) => {
    if (line === null) return '';
    const text = isEmptyLine(line) ? EMPTY_LINE : line;
    return i === lines.length - 1 ? text : `${text}&nbsp; `;
  }).join('\n');
};

/**
 * A saved comment, ready for the editor: the `&nbsp; ` each line was saved with comes off, so it does not pile up
 * with every edit, and each empty line carries a zero-width space, so the editor - which reads the text as Markdown,
 * where a run of empty lines is one - opens it with every empty line still there.
 */
export const commentForEditing = (body: string): string => {
  const lines = body.replace(/\r\n/g, '\n').split('\n').map(line => line.replace(/(&nbsp;|\u00A0) ?$/, '').replace(/[ \t]+$/, ''));
  return lines.map((text, i) => {
    if (!isEmptyLine(text)) return text;
    // A truly blank line straight after a list is the one that ends it, not one the person typed.
    const previous = lines[i - 1] ?? '';
    if (text === '' && (LIST_ITEM.test(previous) || (/^[ \t]/.test(previous) && previous.trim() !== ''))) return '';
    return ZWSP;
  }).join('\n');
};
